import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserPlusIcon, 
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import UserService from '../../services/UserService';

const TeamManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    lastName: '',
    status: 'active'
  });
  const [userLimit, setUserLimit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const hasReachedLimit = userLimit !== null && users.length >= userLimit;

  // Obtener lista de usuarios
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const result = await UserService.getUsers();
      if (result.success) {
        setUsers(result.users);
        setUserLimit(
          typeof result.limit === 'number' && result.limit >= 0
            ? result.limit
            : null
        );
      } else {
        setUserLimit(null);
        toast.error(result.error || 'Error al cargar usuarios');
      }
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      setUserLimit(null);
      toast.error('Error al cargar el listado de usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenCreateModal = () => {
    if (hasReachedLimit) {
      toast.error('Has alcanzado el límite de usuarios a agregar');
      return;
    }
    setShowCreateModal(true);
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Crear nuevo usuario
  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (hasReachedLimit) {
      toast.error('Has alcanzado el límite de usuarios a agregar');
      return;
    }
    
    // Validar campos usando el servicio
    const validation = UserService.validateUserData(formData);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    setCreating(true);
    
    try {
      const result = await UserService.createUser(formData);
      
      if (result.success) {
        toast.success(result.message);
        setShowCreateModal(false);
        setFormData({ name: '', lastName: '', email: '' });
        // Recargar la lista de usuarios
        await fetchUsers();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error al crear usuario:', error);
      toast.error('Error al crear el usuario. Por favor intenta nuevamente.');
    } finally {
      setCreating(false);
    }
  };

  // Cerrar modal de creación
  const handleCloseModal = () => {
    if (!creating) {
      setShowCreateModal(false);
      setFormData({ name: '', lastName: '', email: '' });
    }
  };

  // Abrir modal de edición
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      lastName: user.last_name,
      status: user.status
    });
    setShowEditModal(true);
  };

  // Verificar si el usuario seleccionado es el usuario actual
  const isCurrentUser = (user) => {
    return currentUser && user && user.email === currentUser.email;
  };

  // Manejar cambios en el formulario de edición
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Actualizar usuario
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    // Validar campos
    if (!editFormData.name || !editFormData.lastName) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    // Verificar si está intentando cambiar su propio estado
    if (isCurrentUser(selectedUser) && editFormData.status !== selectedUser.status) {
      toast.error('No puedes cambiar tu propio estado', {
        duration: 4000,
        icon: '⚠️'
      });
      return;
    }

    setUpdating(true);
    
    try {
      const result = await UserService.updateUser(selectedUser.id, editFormData);
      
      if (result.success) {
        toast.success(result.message);
        setShowEditModal(false);
        setSelectedUser(null);
        setEditFormData({ name: '', lastName: '', status: 'active' });
        // Recargar la lista de usuarios
        await fetchUsers();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      toast.error('Error al actualizar el usuario. Por favor intenta nuevamente.');
    } finally {
      setUpdating(false);
    }
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    if (!updating) {
      setShowEditModal(false);
      setSelectedUser(null);
      setEditFormData({ name: '', lastName: '', status: 'active' });
    }
  };

  // Abrir modal de confirmación de eliminación
  const handleDeleteUser = (user) => {
    // Verificar que no sea el usuario actual
    if (currentUser && user.email === currentUser.email) {
      toast.error('No puedes eliminar tu propia cuenta', {
        duration: 4000,
        icon: '⚠️'
      });
      return;
    }

    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Confirmar eliminación de usuario
  const handleConfirmDelete = async () => {
    if (!userToDelete || deleting) return;

    setDeleting(true);
    const loadingToast = toast.loading('Eliminando usuario...');
    
    try {
      const result = await UserService.deleteUser(userToDelete.id);
      
      if (result.success) {
        toast.success(result.message, { id: loadingToast });
        // Cerrar modal
        setShowDeleteModal(false);
        setUserToDelete(null);
        // Recargar la lista de usuarios
        await fetchUsers();
      } else {
        toast.error(result.error || 'Error al eliminar el usuario', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error('Error al eliminar el usuario', { id: loadingToast });
    } finally {
      setDeleting(false);
    }
  };

  // Cancelar eliminación
  const handleCancelDelete = () => {
    if (!deleting) {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Gestión de Equipos
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Administra los miembros de tu equipo
              </p>
              {userLimit !== null && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Límite de usuarios del equipo: {userLimit}
                </p>
              )}
            </div>
            <button
              onClick={handleOpenCreateModal}
              className={`btn-primary flex items-center space-x-2 ${hasReachedLimit ? 'opacity-60 cursor-not-allowed' : ''}`}
              disabled={hasReachedLimit}
            >
              <UserPlusIcon className="h-5 w-5" />
              <span>Agregar Usuario</span>
            </button>
          </div>
          {hasReachedLimit && (
            <div className="mt-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-200 px-4 py-3 text-sm">
              Has alcanzado el límite de usuarios a agregar.
            </div>
          )}
        </div>

        {/* Tabla de usuarios */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando usuarios...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <UserIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No hay usuarios en el equipo
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Comienza agregando el primer miembro a tu equipo
              </p>
              {!hasReachedLimit ? (
                <button
                  onClick={handleOpenCreateModal}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <UserPlusIcon className="h-5 w-5" />
                  <span>Agregar Primer Usuario</span>
                </button>
              ) : (
                <div className="rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-200 px-4 py-3 text-sm">
                  Has alcanzado el límite de usuarios a agregar.
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Apellido
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Correo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {user.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 flex items-center justify-center">
                              <span className="text-white font-semibold">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {user.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {user.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.status === 'active' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {user.status === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                            title="Editar usuario"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          {currentUser && user.email !== currentUser.email && (
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                              title="Eliminar usuario"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                          {currentUser && user.email === currentUser.email && (
                            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                              (Tú)
                            </span>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6 text-center">
            <div className="text-3xl mb-3">👑</div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Roles y Permisos</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Admin, PM, Developer, QA con permisos personalizados
            </p>
          </div>
          <div className="card p-6 text-center">
            <div className="text-3xl mb-3">🎯</div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Asignación de Tareas</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Asigna tareas y gestiona la carga de trabajo
            </p>
          </div>
          <div className="card p-6 text-center">
            <div className="text-3xl mb-3">💬</div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Menciones</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Menciona usuarios en comentarios y notificaciones
            </p>
          </div>
        </div>
      </motion.div>

      {/* Modal para crear usuario */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={handleCloseModal}
            />
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="card w-full max-w-md p-6 relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header del modal */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Agregar Nuevo Usuario
                    </h3>
                    {!creating && (
                      <button
                        onClick={handleCloseModal}
                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    )}
                  </div>

                  {/* Indicador de carga */}
                  {creating && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        <div>
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                            Procesando solicitud...
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            Esto puede tomar unos segundos. Por favor espera.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Formulario */}
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    {hasReachedLimit && (
                      <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-200 text-sm">
                        Has alcanzado el límite de usuarios a agregar. Cierra este modal para continuar.
                      </div>
                    )}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={creating}
                        className="input-field"
                        placeholder="Ingresa el nombre"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Apellido
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        disabled={creating}
                        className="input-field"
                        placeholder="Ingresa el apellido"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Correo Electrónico
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={creating}
                        className="input-field"
                        placeholder="correo@ejemplo.com"
                        required
                      />
                    </div>

                    {/* Botones */}
                    <div className="flex space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={handleCloseModal}
                        disabled={creating}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={creating || hasReachedLimit}
                        className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {creating ? 'Creando...' : 'Crear Usuario'}
                      </button>
                    </div>
                  </form>
                </motion.div>
              </div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Modal de Edición de Usuario */}
      <AnimatePresence>
        {showEditModal && selectedUser && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={handleCloseEditModal}
            />
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="card w-full max-w-md p-6 relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header del modal */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Editar Usuario
                    </h3>
                    {!updating && (
                      <button
                        onClick={handleCloseEditModal}
                        className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    )}
                  </div>

                  {/* Indicador de carga */}
                  {updating && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        <div>
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                            Actualizando usuario...
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            Esto puede tomar unos segundos. Por favor espera.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Formulario */}
                  <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div>
                      <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre
                      </label>
                      <input
                        type="text"
                        id="edit-name"
                        name="name"
                        value={editFormData.name}
                        onChange={handleEditInputChange}
                        disabled={updating}
                        className="input-field"
                        placeholder="Ingresa el nombre"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="edit-lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Apellido
                      </label>
                      <input
                        type="text"
                        id="edit-lastName"
                        name="lastName"
                        value={editFormData.lastName}
                        onChange={handleEditInputChange}
                        disabled={updating}
                        className="input-field"
                        placeholder="Ingresa el apellido"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="edit-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Estado
                      </label>
                      <select
                        id="edit-status"
                        name="status"
                        value={editFormData.status}
                        onChange={handleEditInputChange}
                        disabled={updating || isCurrentUser(selectedUser)}
                        className="input-field"
                        required
                      >
                        <option value="active">Activo</option>
                        <option value="inactive">Inactivo</option>
                      </select>
                      {isCurrentUser(selectedUser) && (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center">
                          <span className="mr-1">⚠️</span>
                          No puedes cambiar tu propio estado
                        </p>
                      )}
                    </div>

                    {/* Información del usuario (solo lectura) */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Correo electrónico (no editable)</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 font-medium flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          {selectedUser.email}
                        </p>
                      </div>
                    </div>

                    {/* Botones */}
                    <div className="flex space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={handleCloseEditModal}
                        disabled={updating}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={updating}
                        className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updating ? 'Actualizando...' : 'Actualizar Usuario'}
                      </button>
                    </div>
                  </form>
                </motion.div>
              </div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Modal de Confirmación de Eliminación */}
      <AnimatePresence>
        {showDeleteModal && userToDelete && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={handleCancelDelete}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="card w-full max-w-md p-6 relative"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header del modal */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                      <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Eliminar Usuario
                    </h3>
                  </div>
                  {!deleting && (
                    <button
                      onClick={handleCancelDelete}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  )}
                </div>

                {/* Contenido del modal */}
                <div className="mb-6">
                  <p className="text-base text-gray-700 dark:text-gray-300 mb-4">
                    ¿Estás seguro de que deseas eliminar al usuario{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      "{userToDelete.name} {userToDelete.last_name}"
                    </span>
                    ?
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Correo electrónico</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium flex items-center">
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      {userToDelete.email}
                    </p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      ⚠️ Esta acción no se puede deshacer y eliminará permanentemente al usuario del equipo.
                    </p>
                  </div>
                </div>

                {/* Indicador de carga */}
                {deleting && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center space-x-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                          Eliminando usuario...
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Esto puede tomar unos segundos. Por favor espera.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botones */}
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    disabled={deleting}
                    className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {deleting ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamManagement;








